// PRE RMA — Petits composants communs aux écrans de saisie (même comportement partout : saisie rapide, lecture seule, alertes).
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import Icone from './Icones.tsx';

const MOIS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
export const nomMois = (m: number) => MOIS[m - 1] ?? '';
export const fmtNombre = (n: number | null | undefined, dec = 0) =>
  n === null || n === undefined || !Number.isFinite(n) ? '' : n.toLocaleString('fr-FR', { maximumFractionDigits: dec });
export const fmtAr = (n: number | null | undefined) => (n === null || n === undefined ? '' : `${Math.round(n).toLocaleString('fr-FR')} Ar`);

interface NombreProps {
  valeur: number | null | undefined;
  onChange: (v: number | null) => void;
  decimales?: boolean;
  disabled?: boolean;
  placeholder?: string;
  titre?: string;
  classe?: string;
  label: string; // accessibilité (lecteur d'écran) — obligatoire
}

/**
 * Saisie numérique : clavier numérique sur mobile, chiffres seulement, sélection au focus, enregistrement à chaque frappe.
 * Pendant la saisie le texte reste local (les rafraîchissements venant de la base ne doivent jamais écraser ce que l'agent tape).
 */
export function NombreInput({ valeur, onChange, decimales = false, disabled, placeholder, titre, classe, label }: NombreProps) {
  const format = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v).replace('.', decimales ? ',' : '.'));
  const [txt, setTxt] = useState(format(valeur));
  const focus = useRef(false);
  useEffect(() => { if (!focus.current) setTxt(format(valeur)); }, [valeur]);
  return (
    <input
      className={`num ${classe ?? ''}`} inputMode={decimales ? 'decimal' : 'numeric'} aria-label={label} title={titre}
      value={txt} disabled={disabled} placeholder={placeholder}
      onFocus={(e) => { focus.current = true; e.target.select(); }}
      onBlur={() => { focus.current = false; setTxt(format(valeur)); }}
      onChange={(e) => {
        let t = e.target.value.replace(decimales ? /[^0-9.,]/g : /\D/g, '').slice(0, 12);
        if (decimales) { t = t.replace(',', '.'); const i = t.indexOf('.'); if (i >= 0) t = t.slice(0, i + 1) + t.slice(i + 1).replace(/\./g, '').slice(0, 2); }
        setTxt(t.replace('.', decimales ? ',' : '.'));
        const v = t === '' || t === '.' ? null : Number(t);
        onChange(v !== null && Number.isFinite(v) ? v : null);
      }}
    />
  );
}

export function Pastille({ niveau = 'info', children }: { niveau?: 'ok' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  return <span className={`pastille ${niveau}`}>{children}</span>;
}

/**
 * Aide discrète : le texte explicatif est MASQUÉ par défaut (interface épurée) et s'affiche au toucher du « ⓘ ».
 * Rien n'est supprimé : les explications restent disponibles pour qui en a besoin.
 */
export function Aide({ children, libelle = 'Afficher l\'aide' }: { children: ReactNode; libelle?: string }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <span className="aide-info">
      <button type="button" className="aide-bouton" aria-expanded={ouvert} aria-label={libelle} title={libelle} onClick={() => setOuvert((o) => !o)}>ⓘ</button>
      {ouvert && <span className="aide-texte" role="note">{children}</span>}
    </span>
  );
}

export function Message({ niveau, children }: { niveau: 'ok' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  return <div className={`message ${niveau}`} role={niveau === 'bad' ? 'alert' : 'status'}>{children}</div>;
}

export function EnteteModule({ titre, periode, statut, lectureSeule, aide }: { titre: string; periode: { annee: number; mois: number } | null; statut?: string; lectureSeule?: boolean; aide?: ReactNode }) {
  return (
    <div className="entete-module">
      <div>
        <h1>{titre}</h1>
        {periode && <div className="sous-titre">{nomMois(periode.mois)} {periode.annee}{statut ? ` · ${statut}` : ''}</div>}
      </div>
      {lectureSeule && <Pastille niveau="warn">🔒 Lecture seule (mois clôturé)</Pastille>}
      {aide && <div className="aide">{aide}</div>}
    </div>
  );
}

export function Onglets<T extends string>({ onglets, actif, onChoix }: { onglets: Array<{ id: T; label: string; couleur?: string; compteur?: string; erreurs?: number; icone?: string }>; actif: T; onChoix: (id: T) => void }) {
  return (
    <div className="onglets" role="tablist">
      {onglets.map((o) => (
        <button key={o.id} role="tab" aria-selected={o.id === actif} className={`onglet ${o.id === actif ? 'actif' : ''}`} style={{ ['--c' as any]: o.couleur ?? 'var(--couleur-principale)' }} onClick={() => onChoix(o.id)}>
          {o.icone && <Icone nom={o.icone} taille="1.15em" />}{o.label}
          {o.compteur && <span className="compteur">{o.compteur}</span>}
          {!!o.erreurs && <span className="erreurs">{o.erreurs}</span>}
        </button>
      ))}
    </div>
  );
}

export function PasDePeriode() {
  return <div className="carte"><p>Choisissez d'abord le mois de travail sur l'écran <strong>Accueil</strong>.</p></div>;
}

/** Barre fixe en bas d'écran : actions principales toujours à portée du pouce. */
export function BarreActions({ children }: { children: ReactNode }) { return <div className="barre-actions">{children}</div>; }

/**
 * Boîte de confirmation (action irréversible). Le bouton « Annuler » a le focus par défaut : un appui accidentel sur Entrée ne
 * supprime rien. Échap ou un appui à l'extérieur = annuler. Accessible (role=alertdialog, aria-modal).
 */
export function ConfirmDialog({ titre, children, libelleConfirmer = 'Supprimer', libelleAnnuler = 'Annuler', danger = true, enCours = false, motAConfirmer, onConfirmer, onAnnuler }: {
  titre: string; children: ReactNode; libelleConfirmer?: string; libelleAnnuler?: string; danger?: boolean; enCours?: boolean;
  /** Si renseigné (ex. « SUPPRIMER »), le bouton de confirmation reste désactivé tant que l'utilisateur ne l'a pas tapé exactement. */
  motAConfirmer?: string; onConfirmer: () => void; onAnnuler: () => void;
}) {
  const annulerRef = useRef<HTMLButtonElement>(null);
  const [saisie, setSaisie] = useState('');
  const motOk = !motAConfirmer || saisie.trim() === motAConfirmer;
  useEffect(() => {
    annulerRef.current?.focus();
    const touche = (e: KeyboardEvent) => { if (e.key === 'Escape' && !enCours) onAnnuler(); };
    document.addEventListener('keydown', touche);
    return () => document.removeEventListener('keydown', touche);
  }, [enCours]);
  return (
    <div className="dialogue-fond" onMouseDown={(e) => { if (e.target === e.currentTarget && !enCours) onAnnuler(); }}>
      <div className="dialogue" role="alertdialog" aria-modal="true" aria-labelledby="dialogue-titre">
        <h2 id="dialogue-titre">{titre}</h2>
        <div className="dialogue-corps">{children}</div>
        {motAConfirmer && (
          <p>
            <label>Pour confirmer, tapez <strong>{motAConfirmer}</strong> :{' '}
              <input value={saisie} onChange={(e) => setSaisie(e.target.value)} autoComplete="off" autoCapitalize="characters" aria-label={`Taper ${motAConfirmer} pour confirmer`} />
            </label>
          </p>
        )}
        <div className="dialogue-actions">
          <button type="button" ref={annulerRef} className="secondaire" onClick={onAnnuler} disabled={enCours}>{libelleAnnuler}</button>
          <button type="button" className={danger ? 'danger' : ''} onClick={onConfirmer} disabled={enCours || !motOk}>{enCours ? 'Suppression…' : libelleConfirmer}</button>
        </div>
      </div>
    </div>
  );
}
